'use client';

import { Stack, Typography } from '@mui/material';
import type { Notebook } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface DeleteNotebookDialogProps {
  notebook: Notebook;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteNotebookDialog = ({
  notebook,
  workspaceId,
  open,
  onClose,
  onDeleted,
}: DeleteNotebookDialogProps) => {
  const dal = useDAL();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await dal.lakehouse.deleteNotebook(workspaceId, notebook.id);
      onDeleted();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete notebook'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Delete Notebook',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Delete',
          onClick: handleDelete,
          'data-testid': 'confirm-delete-notebook-btn',
          disabled: isLoading,
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-delete-notebook-btn',
        },
      }}
    >
      <Stack gap='16px' padding='0 40px'>
        <Typography>
          Are you sure you want to delete the notebook{' '}
          <strong>{notebook.name}</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone.
        </Typography>
        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
